import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash.clonedeep';
import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { GridApi, GridSync } from '@grid/models';
import { StaticMessage, UtcDateTimeOptions } from '@shared/enums';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprDetailsService } from '@dpr-details/services';
import { ConfirmDialogComponent } from '@shared/components';
import { Constants, getStaticMessage, toUTCDate } from '@shared/utils';
import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import {
  DprDetailsCopyActionModalComponent,
  DprForecastActivitiesGridComponent,
} from '@dpr-details/components';
import { DprForecastActivityGridData } from '@dpr-details/models/grid';
import { DprForecastActivitiesGridSyncResult } from '@dpr-details/models/sync';
import { SyncService } from '@shared/services/grid';
import { DprDetails, DprForecastActivity } from '@shared/models/dpr';
import { DprModalTitlePipe } from '@shared/pipes';
import {
  CopyPreviousDprForecastActivitiesOptions,
  CopyPreviousDprForecastActivitiesResult,
  DprDetailCopyOption,
} from '@dpr-details/models';
import { DprForecastData } from '@dpr-details/enums';
import {
  mapForecastActivitiesToForecastGridData,
  mapSyncForecastActivitiesToSyncForecastGridData,
} from '@dpr-details/utils';
import { ScreenSizeService } from '@shared/services';

@UntilDestroy()
@Component({
  selector: 'app-dpr-forecast-activities-page',
  templateUrl: './dpr-forecast-activities-page.component.html',
  styleUrls: ['./dpr-forecast-activities-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprForecastActivitiesPageComponent
  extends DprDetailsGridContainerBaseComponent<DprForecastActivityGridData>
  implements OnInit
{
  @ViewChild(DprForecastActivitiesGridComponent)
  gridComponent: DprForecastActivitiesGridComponent;

  forecastTitle = $localize`:@@dprDetails.dprForecastActivitiesPage.titleForecast:Forecast activity`;

  canCopyFromPrevious$ = new BehaviorSubject<boolean>(false);

  StaticMessage = StaticMessage;

  private gridReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    protected activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private syncService: SyncService,
    private dprModalTitlePipe: DprModalTitlePipe,
    private datePipe: DatePipe,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute);
    this.dprDetailsRouteParams = this.activatedRoute.parent.params;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.gridData$ = this.dprDetails$.pipe(
      map((dprDetails) => {
        const data = cloneDeep(
          dprDetails.dprForecastActivities
        ) as DprForecastActivity[];

        return data.map((x) => mapForecastActivitiesToForecastGridData(x));
      })
    );

    if (this.customTabNames.forecastTabName) {
      this.forecastTitle = `${
        this.customTabNames.forecastTabName
      } ${$localize`:@@dprDetails.dprForecastActivitiesPage.lblActivity:activity`}`;
    }

    combineLatest([this.dprDetailsOfflineChanges$, this.gridReady$])
      .pipe(
        untilDestroyed(this),
        filter(
          ([dprDetailsOfflineChanges, gridReady]) =>
            !!dprDetailsOfflineChanges && gridReady
        ),
        tap(() => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.applyPredefinedChanges();
          });
        })
      )
      .subscribe();

    combineLatest([
      this.dprDetails$,
      this.selectedSubproject$,
      this.isAppOnline$,
    ])
      .pipe(
        untilDestroyed(this),
        tap(([dprDetails]) => {
          this.shouldDisplayCopyFromPrevious(dprDetails);
        })
      )
      .subscribe();
  }

  save(
    _?,
    _isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<any> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;
    const changes = this.gridComponent.getChanges();

    const validateChanges$ = this.validateChanges(changes);

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(changes, isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  refreshData(): void {
    this.gridComponent.api.reset();
    this.isDirty$.next(false);
  }

  onGridRegistered(gridApi: GridApi<DprForecastActivityGridData>): void {
    this.gridApi = gridApi;

    gridApi.isDirty$
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.isDirty$.next(true);
        })
      )
      .subscribe();

    this.gridReady$.next(true);
  }

  onCopyDprForecastFromPreviousDpr(): void {
    const dialogRef = this.dialog.open(DprDetailsCopyActionModalComponent, {
      ...Constants.DialogOptions,
      disableClose: false,
      data: {
        title: this.dprModalTitlePipe.transform(
          this.dprDetailsService.getModalTitle(
            this.dprDetails.dprDisplayId,
            this.datePipe.transform(
              toUTCDate(
                new Date(this.dprDetails.dprDateTime),
                UtcDateTimeOptions.UtcDateWithoutTime
              ),
              'MMM dd'
            ),
            this.dprDetails.revision
          ),
          `${
            this.customTabNames.forecastTabName
          } ${$localize`:@@dprDetails.dprForecastActivitiesPage.lblActivities:Activities`}`
        ),
        options: [
          {
            id: DprForecastData.Subactivities,
            name: $localize`:@@dprDetails.dprForecastActivitiesPage.txtSubActivities:Sub Activities`,
            value: true,
          },
          {
            id: DprForecastData.CustomData,
            name: $localize`:@@dprDetails.dprForecastActivitiesPage.txtCustomData:Custom Data`,
            value: true,
          },
        ],
        subprojectId: this.selectedSubproject.id,
        dprDetailsId: this.dprDetails.id,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result),
        tap((result: DprDetailCopyOption[]) => {
          this.checkCanCopyDprForecastActivitiesFromPreviousDpr({
            shouldCopySubactivities: result.find(
              (x) => x.id === DprForecastData.Subactivities
            ).value,
            shouldCopyCustomData: result.find(
              (x) => x.id === DprForecastData.CustomData
            ).value,
            subprojectId: this.selectedSubproject.id,
            dprDetailsId: this.dprDetails.id,
          });
        })
      )
      .subscribe();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  //#region Private methods

  private checkCanCopyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): void {
    if (this.isDirty$.value) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        ...Constants.DialogOptions,
        data: {
          title: getStaticMessage(StaticMessage.UnsavedChangesTitle),
          message: getStaticMessage(
            StaticMessage.UnsavedChangesText,
            StaticMessage.CopyText,
            StaticMessage.CopyOperationText
          ),
          buttonLabel: getStaticMessage(StaticMessage.ContinueButton),
        },
      });

      confirmDialog
        .afterClosed()
        .pipe(
          untilDestroyed(this),
          filter((result) => result),
          switchMap(() =>
            this.copyDprForecastActivitiesFromPreviousDpr(options)
          )
        )
        .subscribe();
    } else {
      this.copyDprForecastActivitiesFromPreviousDpr(options).subscribe();
    }
  }

  private copyDprForecastActivitiesFromPreviousDpr(
    options: CopyPreviousDprForecastActivitiesOptions
  ): Observable<CopyPreviousDprForecastActivitiesResult> {
    return this.dprDetailsService
      .copyDprForecastActivitiesFromPreviousDpr(options)
      .pipe(
        untilDestroyed(this),
        tap((result) => {
          this.offlineFacade.setDprForecastActivities(
            result.dprForecastActivities,
            result.firstWarning,
            result.warningStatusTab,
            result.dprDetailId
          );
          this.refreshData();
        })
      );
  }

  private saveChanges(
    changes: GridSync<DprForecastActivityGridData>,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprForecastActivitiesGridSyncResult> {
    const syncData =
      this.dprDetailsSyncModelBuilderService.forDprForecastActivities(
        this.selectedSubproject.id,
        this.dprDetails.id,
        changes
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprForecastActivities(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.forecastTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprForecastActivitiesGridSyncResult): void {
    this.offlineFacade.setDprForecastActivities(
      result.dprForecastActivities,
      result.firstWarning,
      result.warningStatusTab,
      this.dprDetails.id
    );

    this.refreshData();
  }

  private validateChanges(
    changes: GridSync<DprForecastActivityGridData>
  ): Observable<boolean> {
    return of(this.gridComponent.validateChanges(changes));
  }

  private applyPredefinedChanges(): void {
    const offlineChanges =
      (mapSyncForecastActivitiesToSyncForecastGridData(
        this.dprDetailsOfflineChanges?.dprForecastActivities
      ) as GridSync<DprForecastActivityGridData>) ??
      this.syncService.getDefaultSyncModel();

    this.gridApi.setPredefinedChanges(offlineChanges);

    for (const data of this.gridComponent.gridData) {
      this.setNestedPredefinedChanges(data, offlineChanges);
    }
  }

  private setNestedPredefinedChanges(
    data: DprForecastActivityGridData,
    offlineDataChanges: GridSync<DprForecastActivityGridData>
  ): void {
    const isAdded = offlineDataChanges?.addedList?.length;
    const isUpdated = offlineDataChanges?.updatedList?.length;

    const changedItem = isAdded
      ? offlineDataChanges?.addedList[0]
      : isUpdated
      ? offlineDataChanges?.updatedList[0]
      : null;

    if (!changedItem) {
      return;
    }

    data.syncDprForecastActivityCustomDataList =
      changedItem.syncDprForecastActivityCustomDataList;
    data.syncDprForecastSubActivities =
      changedItem.syncDprForecastSubActivities;
  }

  private shouldDisplayCopyFromPrevious(dprDetails: DprDetails): void {
    this.dprDetailsService
      .getPreviousDprDetailsId(dprDetails.dprDateTime)
      .pipe(
        untilDestroyed(this),
        tap((prevDprId) => {
          this.canCopyFromPrevious$.next(!!prevDprId);
        })
      )
      .subscribe();
  }

  //#endregion
}
